import type { AddOn, Chef, ChefMenu, Fulfillment, GroceryPolicy } from '../types/chef';
import type { Quote, QuoteLine } from '../types/booking';

export const SERVICE_FEE_RATE = 0.1;
/** Thai VAT. */
export const TAX_RATE = 0.07;

export const GROCERY_LABEL: Record<GroceryPolicy, string> = {
  included: 'Groceries included',
  at_cost: 'Groceries at cost',
  estimate: 'Groceries estimated',
};

export function inHomeMenus(chef: Chef): ChefMenu[] {
  return chef.menus.filter((m) => m.fulfillment.includes('in_home'));
}

export function deliveryMenus(chef: Chef): ChefMenu[] {
  return chef.menus.filter((m) => m.fulfillment.includes('delivery'));
}

/** The normalized "from" price every listing is compared on. */
export function fromPricePerGuest(chef: Chef): number {
  const menus = inHomeMenus(chef);
  if (menus.length) return Math.min(...menus.map((m) => m.pricePerGuest));
  const delivery = deliveryMenus(chef);
  return delivery.length ? Math.min(...delivery.map((m) => m.pricePerPortion ?? m.pricePerGuest)) : 0;
}

export function fromPriceUnit(chef: Chef): 'guest' | 'portion' {
  return inHomeMenus(chef).length ? 'guest' : 'portion';
}

export function cheapestMenu(chef: Chef): ChefMenu {
  return [...chef.menus].sort((a, b) => a.pricePerGuest - b.pricePerGuest)[0];
}

export function signatureMenu(chef: Chef): ChefMenu {
  return chef.menus.find((m) => m.signature) ?? chef.menus[0];
}

export function menuQualifier(menu: ChefMenu): string {
  const parts = [`min ${menu.minGuests} guests`, `${menu.courses.length} courses`];
  if (menu.fulfillment.includes('in_home')) parts.push(`${menu.includedHours} hrs`);
  parts.push(GROCERY_LABEL[menu.groceries].toLowerCase());
  return parts.join(' · ');
}

export interface AddOnSelection {
  addOnId: string;
  qty?: number;
}

export interface QuoteDraft {
  menuId: string;
  fulfillment: Fulfillment;
  guests: number;
  date: string;
  time: string;
  hours: number;
  addOns: AddOnSelection[];
  travelKm: number;
  note: string;
  allergiesAcknowledged: boolean;
  /** Guest-proposed menu, when they did not pick one of the chef's. */
  customBrief?: string;
  budgetPerGuest?: number;
}

export interface PricedQuote {
  lines: QuoteLine[];
  subtotal: number;
  serviceFee: number;
  tax: number;
  total: number;
  depositDue: number;
  balanceDue: number;
  minimumShortfall: number;
}

function addOnQty(addOn: AddOn, draft: QuoteDraft, selection: AddOnSelection): number {
  if (selection.qty) return selection.qty;
  switch (addOn.unit) {
    case 'per_guest':
      return draft.guests;
    case 'per_hour':
      return 1;
    case 'per_km':
      return draft.travelKm;
    default:
      return 1;
  }
}

function unitLabel(addOn: AddOn, qty: number): string {
  switch (addOn.unit) {
    case 'per_guest':
      return `${qty} guests × ${addOn.price}`;
    case 'per_hour':
      return `${qty} hr${qty === 1 ? '' : 's'} × ${addOn.price}`;
    case 'per_km':
      return `${qty} km × ${addOn.price}`;
    default:
      return 'flat fee';
  }
}

export function priceQuote(chef: Chef, draft: QuoteDraft): PricedQuote {
  const menu = chef.menus.find((m) => m.id === draft.menuId) ?? chef.menus[0];
  const lines: QuoteLine[] = [];

  const isDelivery = draft.fulfillment === 'delivery';
  const unitPrice = isDelivery ? menu.pricePerPortion ?? menu.pricePerGuest : menu.pricePerGuest;

  lines.push({
    id: 'menu',
    kind: 'menu',
    label: menu.name,
    detail: `${draft.guests} ${isDelivery ? 'portions' : 'guests'} × ${unitPrice}`,
    qty: draft.guests,
    unitPrice,
    amount: unitPrice * draft.guests,
  });

  if (!isDelivery) {
    const extraHours = Math.max(0, draft.hours - menu.includedHours);
    if (extraHours > 0) {
      const hourly = chef.addOns.find((a) => a.unit === 'per_hour');
      const rate = hourly?.price ?? 55;
      lines.push({
        id: 'extra-hours',
        kind: 'addon',
        label: 'Additional service hours',
        detail: `${extraHours} hr${extraHours === 1 ? '' : 's'} beyond the ${menu.includedHours} included`,
        qty: extraHours,
        unitPrice: rate,
        amount: rate * extraHours,
      });
    }
  }

  draft.addOns.forEach((selection) => {
    const addOn = chef.addOns.find((a) => a.id === selection.addOnId);
    if (!addOn || addOn.unit === 'per_km') return;
    const qty = addOnQty(addOn, draft, selection);
    if (qty <= 0) return;
    lines.push({
      id: addOn.id,
      kind: 'addon',
      label: addOn.name,
      detail: unitLabel(addOn, qty),
      qty,
      unitPrice: addOn.price,
      amount: addOn.price * qty,
    });
  });

  if (isDelivery) {
    if (draft.travelKm > chef.deliveryFreeRadiusKm) {
      lines.push({
        id: 'delivery',
        kind: 'travel',
        label: 'Delivery',
        detail: `${draft.travelKm} km · free within ${chef.deliveryFreeRadiusKm} km`,
        qty: 1,
        unitPrice: chef.deliveryFee,
        amount: chef.deliveryFee,
      });
    }
  } else {
    const billableKm = Math.max(0, Math.round(draft.travelKm - chef.travelIncludedKm));
    if (billableKm > 0) {
      lines.push({
        id: 'travel',
        kind: 'travel',
        label: 'Travel beyond included area',
        detail: `${billableKm} km × ${chef.travelPerKm} · ${chef.travelIncludedKm} km included`,
        qty: billableKm,
        unitPrice: chef.travelPerKm,
        amount: billableKm * chef.travelPerKm,
      });
    }
  }

  if (menu.groceries === 'estimate') {
    const per = menu.groceryEstimatePerGuest ?? 0;
    lines.push({
      id: 'groceries',
      kind: 'groceries',
      label: 'Grocery estimate',
      detail: `${draft.guests} × ${per} · trued up to receipts`,
      qty: draft.guests,
      unitPrice: per,
      amount: per * draft.guests,
    });
  } else if (menu.groceries === 'at_cost') {
    lines.push({
      id: 'groceries',
      kind: 'groceries',
      label: 'Groceries at cost',
      detail: 'Billed after the booking with receipts attached',
      qty: 1,
      unitPrice: 0,
      amount: 0,
    });
  }

  const subtotal = lines.reduce((sum, line) => sum + line.amount, 0);
  const serviceFee = Math.round(subtotal * SERVICE_FEE_RATE);
  const tax = Math.round((subtotal + serviceFee) * TAX_RATE);
  const total = subtotal + serviceFee + tax;
  const depositDue = Math.round((total * chef.depositPercent) / 100);

  return {
    lines,
    subtotal,
    serviceFee,
    tax,
    total,
    depositDue,
    balanceDue: total - depositDue,
    minimumShortfall: Math.max(0, chef.minimumSpend - subtotal),
  };
}

export function quoteFromDraft(
  chef: Chef,
  draft: QuoteDraft,
  meta: { id: string; version: number; bookingId: string; customerId: string; expiresAt: string },
): Quote {
  const menu = chef.menus.find((m) => m.id === draft.menuId) ?? chef.menus[0];
  const priced = priceQuote(chef, draft);
  return {
    id: meta.id,
    version: meta.version,
    chefId: chef.id,
    customerId: meta.customerId,
    bookingId: meta.bookingId,
    menuId: menu.id,
    menuName: menu.name,
    fulfillment: draft.fulfillment,
    guests: draft.guests,
    date: draft.date,
    time: draft.time,
    hours: draft.hours,
    groceries: menu.groceries,
    lines: priced.lines,
    subtotal: priced.subtotal,
    serviceFee: priced.serviceFee,
    tax: priced.tax,
    total: priced.total,
    depositPercent: chef.depositPercent,
    depositDue: priced.depositDue,
    balanceDue: priced.balanceDue,
    expiresAt: meta.expiresAt,
    status: 'pending',
    note: draft.note,
    allergiesAcknowledged: draft.allergiesAcknowledged,
    createdAt: new Date().toISOString(),
  };
}

/** Equipment a menu needs that the customer's kitchen does not list. */
export function missingEquipment(menu: ChefMenu, available: { id: string; available: boolean }[]): string[] {
  return menu.equipmentNeeded.filter((id) => !available.some((e) => e.id === id && e.available));
}
